import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { generateClient } from 'aws-amplify/data';
import { v4 as uuidv4 } from 'uuid';
import { formatNYDateTime, formatNYDate } from '../utils/datetime';
import './CaseDetails.css';

const client = generateClient({ authMode: 'userPool' });

// Helper function to format date-only fields without applying a local timezone shift.
function formatDateNoTZ(isoString) {
  if (!isoString) return '-';
  const dateObj = new Date(isoString);
  // Adjust for timezone offset so we get the original date (ignoring local time)
  dateObj.setMinutes(dateObj.getMinutes() + dateObj.getTimezoneOffset());

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${month}/${day}/${year}`;
}

const formatValue = (key, value) => {
  if (!value) return '-';

  // For purely date-only fields, use the helper to avoid time shifts:
  if (key === 'dob' || key === 'violationDate') {
    return formatDateNoTZ(value);
  }

  if (key === 'courtDateTime') {
    return formatNYDateTime(value);
  }

  if (['totalFee', 'paidAmount', 'amountDue'].includes(key)) {
    return `$${parseFloat(value).toFixed(2)}`;
  }

  return value.toString();
};

const calculateAmountDue = (data) => {
  const total = parseFloat(data?.totalFee) || 0;
  const paid = parseFloat(data?.paidAmount) || 0;
  return (total - paid).toFixed(2);
};

const CaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateCase, deleteCase, getAllCases } = useAppContext();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [relatedCases, setRelatedCases] = useState([]);

  useEffect(() => {
    const loadCase = async () => {
      try {
        setLoading(true);
        const res = await client.models.Case.get({ id });
        setCaseData(res?.data || null);
      } catch (err) {
        console.error('❌ Failed to load case:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCase();
  }, [id]);

  // Load all cases and filter for related cases once the current case is loaded.
  useEffect(() => {
    if (caseData && getAllCases) {
      getAllCases()
        .then((casesList) => {
          console.log('🔍 Total cases loaded:', casesList.length);
          console.log('🔍 Current case:', {
            id: caseData.id,
            firstName: caseData.firstName,
            lastName: caseData.lastName,
            dob: caseData.dob
          });

          const currentDob = caseData.dob
            ? new Date(caseData.dob).toISOString().split('T')[0]
            : '';
          
          const related = casesList.filter((c) => {
            if (c.id === caseData.id) return false; // Skip current case
            
            const cDob = c.dob ? new Date(c.dob).toISOString().split('T')[0] : '';
            
            // More flexible matching - handle case variations and trim whitespace
            const firstNameMatch = (c.firstName || '').trim().toLowerCase() === (caseData.firstName || '').trim().toLowerCase();
            const lastNameMatch = (c.lastName || '').trim().toLowerCase() === (caseData.lastName || '').trim().toLowerCase();
            
            // Primary matching: exact DOB match
            const exactDobMatch = cDob === currentDob;
            
            // Fallback matching: same name but allow for missing or different DOB
            // This catches cases where DOB might be missing or formatted differently
            const nameOnlyMatch = firstNameMatch && lastNameMatch && (!currentDob || !cDob);
            
            const isRelated = firstNameMatch && lastNameMatch && (exactDobMatch || nameOnlyMatch);
            
            if (isRelated) {
              console.log('✅ Found related case:', {
                id: c.id,
                firstName: c.firstName,
                lastName: c.lastName,
                dob: c.dob,
                appearanceNumber: c.appearanceNumber,
                matchType: exactDobMatch ? 'exact DOB' : 'name only'
              });
            }
            
            return isRelated;
          });
          
          // Sort related cases by court date (soonest first), then by creation date
          const sortedRelated = related.sort((a, b) => {
            // First, sort by court date if both have dates
            if (a.courtDateTime && b.courtDateTime) {
              return new Date(a.courtDateTime) - new Date(b.courtDateTime);
            }
            // Cases with court dates come before those without
            if (a.courtDateTime && !b.courtDateTime) return -1;
            if (!a.courtDateTime && b.courtDateTime) return 1;
            
            // If neither has court date, sort by creation date (newest first)
            const aDate = new Date(a.createdAt || a.updatedAt || 0);
            const bDate = new Date(b.createdAt || b.updatedAt || 0);
            return bDate - aDate;
          });
          
          console.log('🔍 Total related cases found:', sortedRelated.length);
          setRelatedCases(sortedRelated);
        })
        .catch((err) => console.error('❌ Error loading related cases:', err));
    }
  }, [caseData, getAllCases]);

  const handleBack = () => {
    navigate(location.state?.fromEdit ? -2 : -1);
  };

  const handleDuplicate = async () => {
    if (!caseData) return;

    const fieldsToCopy = [
      'firstName',
      'lastName',
      'dob',
      'phone',
      'secondaryPhone',
      'tertiaryPhone',
      'email',
      'mailingAddress',
      'incidentCounty',
      'caseType',
      'officerName',
      'appearanceNumber',
      'idnumber',
      'courtDateTime',
      'violationDate',
      'status',
      'totalFee',
      'paidAmount',
      'amountDue',
      'balanceDue',
      'comments',
    ];

    const duplicate = { id: uuidv4(), status: 'Open' };
    fieldsToCopy.forEach((key) => (duplicate[key] = caseData[key]));

    try {
      await updateCase(duplicate);
      navigate(`/case/${duplicate.id}`);
    } catch (err) {
      console.error('❌ Duplication failed:', err);
    }
  };

  const handleDelete = async () => {
    if (confirmText === 'delete') {
      await deleteCase(id);
      navigate('/search');
    }
  };

  const handleRelatedCaseChange = (e) => {
    const selectedId = e.target.value;
    if (selectedId) {
      navigate(`/case/${selectedId}`);
    }
  };

  if (loading) return <div className="container">Loading...</div>;
  if (!caseData) return <div className="container">Case not found.</div>;

  const balanceStatus = calculateAmountDue(caseData) === '0.00' ? 'PAID IN FULL' : 'NOT PAID';

  return (
    <div className="container case-details-page">
      <div className="case-details-header">
        <button className="back-button no-print" onClick={handleBack}>
          ← Back
        </button>
        <h2>Case Details</h2>
        <button className="print-button no-print" onClick={() => window.print()}>
          🖨️ Print Case
        </button>
      </div>

      {/* Action buttons */}
      <div className="action-buttons no-print">
        <Link to={`/case/${id}/edit`}>
          <button className="edit-button">Edit Case</button>
        </Link>
        <button className="duplicate-button" onClick={handleDuplicate}>
          Duplicate Case
        </button>
        {!showConfirm ? (
          <button className="delete-button" onClick={() => setShowConfirm(true)}>
            Delete Case
          </button>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <p>
              Type <strong>delete</strong> to confirm:
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <button
              disabled={confirmText !== 'delete'}
              onClick={handleDelete}
              style={{ backgroundColor: 'crimson', color: 'white', marginLeft: '1rem' }}
            >
              Confirm Delete
            </button>
          </div>
        )}

        {/* Related Cases Dropdown, label on top of the dropdown */}
        <div className="related-cases-dropdown">
          <label htmlFor="related-cases">Other Tickets by this Client:</label>
          <select
            id="related-cases"
            onChange={handleRelatedCaseChange}
            defaultValue=""
          >
            <option value="">Select related case</option>
            {relatedCases.map((rc) => {
              const courtDate = rc.courtDateTime
                ? formatNYDate(rc.courtDateTime, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'No Date';
              const violationType = rc.caseType || 'Unknown';
              const ticketNumber = rc.appearanceNumber || 'No Ticket';
              const disposition = rc.status || 'No Status';
              
              return (
                <option key={rc.id} value={rc.id}>
                  {courtDate} - {violationType} ({ticketNumber}) - {disposition}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="details-grid">
        {/* Match Intake/Edit Form order */}
        <div className="detail-item">
          <strong>First Name</strong>
          <span>{formatValue('firstName', caseData.firstName)}</span>
        </div>
        <div className="detail-item">
          <strong>Ticket #</strong>
          <span>{formatValue('appearanceNumber', caseData.appearanceNumber)}</span>
        </div>
        <div className="detail-item">
          <strong>Court Date</strong>
          <span>{formatValue('courtDateTime', caseData.courtDateTime)}</span>
        </div>

        <div className="detail-item">
          <strong>Last Name</strong>
          <span>{formatValue('lastName', caseData.lastName)}</span>
        </div>
        <div className="detail-item">
          <strong>Officer Name</strong>
          <span>{formatValue('officerName', caseData.officerName)}</span>
        </div>
        <div className="detail-item">
          <strong>County</strong>
          <span>{formatValue('incidentCounty', caseData.incidentCounty)}</span>
        </div>

        <div className="detail-item">
          <strong>Phone</strong>
          <span>{formatValue('phone', caseData.phone)}</span>
        </div>
        <div className="detail-item financial">
          <strong>Total Fee</strong>
          <span>{formatValue('totalFee', caseData.totalFee)}</span>
        </div>
        <div className="detail-item">
          <strong>Violation Type</strong>
          <span>{formatValue('caseType', caseData.caseType)}</span>
        </div>

        <div className="detail-item">
          <strong>Secondary Phone</strong>
          <span>{formatValue('secondaryPhone', caseData.secondaryPhone)}</span>
        </div>
        <div className="detail-item financial">
          <strong>Paid Amount</strong>
          <span>{formatValue('paidAmount', caseData.paidAmount)}</span>
        </div>
        <div className="detail-item">
          <strong>Violation Date</strong>
          <span>{formatValue('violationDate', caseData.violationDate)}</span>
        </div>

        <div className="detail-item">
          <strong>Email Address</strong>
          <span>{formatValue('email', caseData.email)}</span>
        </div>
        <div className="detail-item">
          <strong>Amount Due</strong>
          <span>{formatValue('calculateAmountDue', calculateAmountDue(caseData))}</span>
        </div>

        <div className="detail-item">
          <strong>Disposition</strong>
          <span>{formatValue('status', caseData.status)}</span>
        </div>

        <div className="detail-item">
          <strong>Mailing Address</strong>
          <span>{formatValue('mailingAddress', caseData.mailingAddress)}</span>
        </div>
        <div className="detail-item">
          <strong>Balance Due?</strong>
          <span
            style={{
              color: balanceStatus === 'PAID IN FULL' ? 'green' : 'red',
              fontWeight: 'bold',
            }}
          >
            {balanceStatus}
          </span>
        </div>
        <div className="detail-item">
          <strong>Date of Birth</strong>
          <span>{formatValue('dob', caseData.dob)}</span>
        </div>
        <div className="detail-item">
          <strong>ID#</strong>
          <span>{formatValue('idnumber', caseData.idnumber)}</span>
        </div>

        {/* Full width for comments */}
        <div className="detail-item full-width">
          <strong>Comments</strong>
          <span>{formatValue('comments', caseData.comments)}</span>
        </div>
      </div>
    </div>
  );
};

export default CaseDetails;
